// @ts-nocheck
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { imageStyles } from "@store/Themes/common/mixins/image";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import updateCssValue, { Operators } from "@toolkit/Framework/Common/Utilities/updateCssValue";
import { focusStyles, gridGapStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import { css } from "styled-components";

export interface CategoryListProps {
    title?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    noResult?: TypographyPresentationProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    moreGridItem?: GridItemProps;
    moreButton?: ButtonPresentationProps;
    itemLink?: LinkPresentationProps;
    itemImageWrapper?: InjectableCss;
    itemImage?: LazyImageProps;
    itemTitle?: TypographyPresentationProps;
    itemList?: TypographyProps;
    itemListItem?: TypographyProps;
    itemListLink?: LinkPresentationProps;
    itemListMoreLink?: LinkPresentationProps;
}

const horizontalGap = pxToRemArray([10]); /* Store Custom Code */
const verticalGap = pxToRemArray([20, 40]);

export const categoryListProps: CategoryListProps = {
    title: { variant: "h2" },
    loadingOverlay: { minHeight: 100 },
    noResult: typographyGetNoResultsProps(),
    gridContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(horizontalGap, verticalGap)}
            ${breakpointValues(verticalGap, ["margin-top", "margin-bottom"])}

            &:first-child {
                margin-top: 0;
            }

            &:last-child {
                margin-bottom: 0;
            }
        `,
        offsetProps: {
            as: "ul",
        },
    },
    gridItem: {
        as: "li",
        width: [12, 6, 4, 3, 3],
        css: css`
            position: relative;
            flex-direction: column;

            /* Store Removed Code - &::before */

            ${breakpoint("sm")} {
                &::before {
                    display: block;
                }
            }

            ${breakpoint("sm", "only")} {
                &:nth-child(2n + 1)::before {
                    display: none;
                }
            }

            ${breakpoint("md", "only")} {
                &:nth-child(3n + 1)::before {
                    display: none;
                }
            }

            ${breakpoint("lg")} {
                &:nth-child(4n + 1)::before {
                    display: none;
                }
            }
        `,
    },
    moreGridItem: {
        as: "li",
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    moreButton: {
        variant: "tertiary",
        css: css`
            ${x => x.theme.button.tertiary.css}
            width: 100%;

            ${breakpoint("md")} {
                width: auto;
            }
        `,
    },
    itemLink: {
        typographyProps: {
            css: css`
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
            `,
        },
        css: css`
            ${focusStyles()}
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            background: ${getColor("common.background")};
            /* Store Custom Code - Start */
            border: ${pxToRem(1)} solid ${getColor("common.border")};
            border-bottom: ${pxToRem(5)} solid ${getColor("common.border")};
            /* Store Custom Code - End */
            color: ${getColor("text.linkAlt")};
            transition: ${transitionHoverStyles("color", "border-color")};

            &:hover,
            &:active {
                /* Store Custom Code */
                color: ${getColor("text.linkHover")};
                border-bottom-color: ${getColor("text.linkHover")};
            }
        `,
    },
    itemImageWrapper: {
        css: css`
            ${x => imageStyles(x.theme.design.category.image.small.ratio)}
            max-height: ${pxToRem(240)};
        `,
    },
    itemImage: {
        css: css`
            ${x => x.theme.lazyImage.defaultProps?.css}
            background-color: transparent;
        `,
    },
    itemTitle: {
        forwardAs: "h2",
        css: css`
            ${spacingStyles("1em", null)}
            /* Store Custom Code - Start */
            padding-bottom: 1em;
            flex-grow: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            /* Store Custom Code - End */

            ${({ theme }) => css`
                font-size: ${theme.design.typography.size.base};
                font-weight: ${theme.design.typography.weight.bold};
                line-height: ${theme.design.typography.lineHeight.semi};
            `}
        `,
    },
    itemList: {
        as: "ul",
        css: css`
            width: 100%;
        `,
    },
    itemListItem: {
        as: "li",
    },
    itemListLink: {
        icon: {
            position: "right",
            iconProps: {
                src: ArrowForward,
                size: 22,
                css: css`
                    color: ${getColor("common.border")};

                    a:hover > & {
                        color: inherit;
                    }
                `,
            },
        },
        css: css`
            ${focusStyles()}
            display: flex;
            justify-content: space-between;
            padding: ${pxToRem(12, 0)};
            color: ${getColor("text.linkAlt")};
            border-bottom: ${pxToRem(1)} solid ${getColor("common.borderLight")};
            transition: ${transitionHoverStyles("color", "border-color")};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};

            &:hover,
            &:active {
                color: ${getColor("text.linkAltHover")};
                border-color: ${getColor("text.linkAltHover")};
            }
        `,
    },
    itemListMoreLink: {
        css: css`
            ${linkActionStyles()}
            margin-top: ${pxToRem(20)};
        `,
    },
};
